import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

// Usar as variáveis de ambiente corretas
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export type TabType = 'all' | 'people' | 'posts';

export interface SearchResult {
  id: string;
  type: 'user' | 'post';
  full_name?: string;
  username?: string;
  avatar_url?: string;
  academy_name?: string;
  belt_rank?: string;
  content?: string;
  created_at?: string;
  visibility?: 'public' | 'connections' | 'private';
  likes_count?: number;
  comments_count?: number;
  author?: {
    id: string;
    full_name: string;
    username: string;
    avatar_url?: string;
    belt_rank?: string;
    academy_name?: string;
  };
}

export const useSearch = (query: string, tab: TabType, userId?: string) => {
  const { user } = useAuth();

  // Função para buscar usuários
  const searchUsers = async (searchTerm: string, page: number = 1, limit: number = 20, userId?: string) => {
    try {
      // Sempre tentar obter o token da sessão atual primeiro
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        // Se não há token, usar requisição anônima
        const response = await fetch(`${SUPABASE_URL}/functions/v1/search_users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY || ''
          },
          body: JSON.stringify({ searchTerm, page, limit, userId })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
      }

      // Se há token, usar requisição autenticada
      const response = await fetch(`${SUPABASE_URL}/functions/v1/search_users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify({ searchTerm, page, limit, userId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Search users error:', error);
      throw error;
    }
  };

  // Função para buscar posts
  const searchPosts = async (searchTerm: string, page: number = 1, limit: number = 20, userId?: string) => {
    try {
      // Sempre tentar obter o token da sessão atual primeiro
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        // Se não há token, usar requisição anônima
        const response = await fetch(`${SUPABASE_URL}/functions/v1/search_posts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY || ''
          },
          body: JSON.stringify({ searchTerm, page, limit, userId })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
      }

      // Se há token, usar requisição autenticada
      const response = await fetch(`${SUPABASE_URL}/functions/v1/search_posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify({ searchTerm, page, limit, userId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Search posts error:', error);
      throw error;
    }
  };

  return useInfiniteQuery({
    queryKey: ['search', query, tab, userId],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = pageParam as number;
      
      // Removido: saveSearchTerm não deve ser chamado automaticamente aqui
      // Ele deve ser chamado apenas quando o usuário pressionar Enter
      
      switch (tab) {
        case 'people': {
          const result = await searchUsers(query, page, 20, userId);
          if (!result) return [];
          return result.data.map(user => ({
            id: user.id,
            type: 'user' as const,
            full_name: user.name,
            username: user.username,
            avatar_url: user.avatar_url,
            academy_name: user.academy_name,
            belt_rank: user.belt_rank
          }));
        }
        case 'posts': {
          const result = await searchPosts(query, page, 20, userId);
          if (!result) return [];
          return result.data.map(post => ({
            id: post.id,
            type: 'post' as const,
            content: post.content,
            created_at: post.created_at,
            visibility: post.visibility as 'public' | 'connections' | 'private',
            likes_count: 0, // TODO: Implementar contagem de likes
            comments_count: 0, // TODO: Implementar contagem de comentários
            author: {
              id: post.author.id,
              full_name: post.author.name,
              username: post.author.username,
              avatar_url: post.author.avatar_url,
              belt_rank: post.author.belt_rank,
              academy_name: post.author.academy_name
            }
          }));
        }
        case 'all':
        default: {
          const [usersResult, postsResult] = await Promise.all([
            searchUsers(query, page, 10, userId),
            searchPosts(query, page, 10, userId)
          ]);
          
          const users = usersResult?.data.map(user => ({
            id: user.id,
            type: 'user' as const,
            full_name: user.name,
            username: user.username,
            avatar_url: user.avatar_url,
            academy_name: user.academy_name,
            belt_rank: user.belt_rank
          })) || [];
          
          const posts = postsResult?.data.map(post => ({
            id: post.id,
            type: 'post' as const,
            content: post.content,
            created_at: post.created_at,
            visibility: post.visibility as 'public' | 'connections' | 'private',
            likes_count: 0, // TODO: Implementar contagem de likes
            comments_count: 0, // TODO: Implementar contagem de comentários
            author: {
              id: post.author.id,
              full_name: post.author.name,
              username: post.author.username,
              avatar_url: post.author.avatar_url,
              belt_rank: post.author.belt_rank,
              academy_name: post.author.academy_name
            }
          })) || [];
          
          // Combinar e ordenar por relevância
          const combined = [...users, ...posts];
          return combined.slice(0, 20);
        }
      }
    },
    getNextPageParam: (lastPage: SearchResult[], allPages) => {
      return lastPage.length === 20 ? allPages.length + 1 : undefined;
    },
    enabled: query.trim().length > 0, // Removido a condição !!userId
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}; 